import { ORDER_ORIGIN } from '../../../../__queries__';

export interface BaseRefund {
  /** When the payment was made - this is needed for some payment methods */
  transactionCreatedAt: string;

  /** The payment transaction ID we are refunding */
  transactionId: string;

  /** The payment method we are refunding */
  method: string;

  /** The waiter who took the payment when it was made */
  waiterId: string;

  origin: ORDER_ORIGIN;

  orderId: string;

  /**
   * Hotel room only: the refund reopens the cheque (requirement 2.10), so the
   * hotel takes back the whole charge as a reopen, same business day only.
   */
  reopen?: boolean;

  /** Called once the payment method has refunded, before the order is refunded in Baemingo */
  onPaymentRefunded?: () => void;

  onCompleted: (receiptError?: string) => void;

  onFail: (message?: string) => void;
}

export interface RefundProps extends BaseRefund {
  amount: number;

  /**
   * The items being refunded, on ITEM and ORDER refunds. Only Klarna needs
   * these - Adyen rejects a partial refund of an open invoice payment without
   * the returned lines, because Klarna reissues the shopper's invoice from
   * them. Undefined on tip refunds, which have no items.
   */
  items?: Item[];

  /**
   * Whether this refund covers the whole remaining payment. A full refund
   * needs no line items; a partial one does.
   */
  isFullRefund: boolean;

  /** Only the tip is refunded: `amount` is how much it went down, and there are no items */
  isTipRefund: boolean;
}

export interface Item {
  id: string;

  // We dont use this field right now, but its easier to understand
  // what "ID" is if we also add "itemId" (it's the ID of the OrderItem)
  itemId: string;

  /**
   * For some reason we need this to print the receipt.
   * I am not sure why.
   */
  entryId: string;

  categoryId: string;
  categoryName: string;
  vatRate: number;

  amount: number;
  discount: number;

  /**
   * Both of these are for printing the receipt
   */
  fraction: number;
  title: string;
}

export interface ItemRefund extends BaseRefund {
  type: 'ITEM';

  /** All the items to refund */
  items: Item[];
}

export interface OrderRefund extends BaseRefund {
  type: 'ORDER';

  /**
   * Important:
   * This is an array of the items that are not
   * yet refunded on the order.
   */
  items: Item[];

  /** On order refunds, also refund tip */
  tipAmount: number;
}

export interface TipRefund extends BaseRefund {
  type: 'TIP';

  origin: ORDER_ORIGIN;

  orderId: string;

  /** The tip after refunds */
  newTipAmount: number;

  /** What the tip was before the refund */
  previousTipAmount: number;
}

export type Refund = TipRefund | ItemRefund | OrderRefund;

export interface AdeynTerminal {
  IP: string | null;
  POIID: string;
}

export enum PAYMENT_TYPE {
  IZETTLE = 'IZETTLE',
  ADYEN = 'ADYEN',
  MICRODEB = 'MICRODEB',
  SURFBOARD = 'SURFBOARD',
  APP = 'APP',
  MULTIPAY = 'MULTIPAY',
  BAEMINGO_GIFTCARD = 'BAEMINGO_GIFTCARD',
  RETAIN24 = 'RETAIN24',
  CASH = 'CASH',
  CRYPTO = 'CRYPTO',
  WAITERAID = 'WAITERAID',
  KLARNA = 'KLARNA',
  HOTEL_ROOM = 'HOTEL_ROOM',
}
